"use client";

import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, CheckCircle } from "lucide-react";

const TOPICS = [
  { value: "feedback", label: "💬 General feedback" },
  { value: "bug",      label: "🐛 Bug report" },
  { value: "feature",  label: "✨ Feature request" },
  { value: "question", label: "❓ Question" },
  { value: "other",    label: "📝 Other" },
];

export default function ContactButton() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("feedback");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);


  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  function handleOpen() {
    setSent(false);
    setError("");
    setMessage("");
    setTopic("feedback");
    setOpen(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setError("");
    setSubmitting(true);

    const res = await fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, topic, message: message.trim() }),
    });

    setSubmitting(false);

    if (!res.ok) {
      const body = await res.json();
      setError(body.error ?? "Something went wrong. Please try again.");
      return;
    }

    setSent(true);
  }

  return (
    <div ref={panelRef} className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 flex flex-col items-end gap-3 pointer-events-none">

      {/* Panel */}
      <div
        className="w-[calc(100vw-2rem)] max-w-sm transition-all duration-200"
        style={{
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0) scale(1)" : "translateY(12px) scale(0.97)",
          pointerEvents: open ? "auto" : "none",
          transformOrigin: "bottom right",
        }}
      >
        <div className="bg-white rounded-3xl shadow-2xl border border-peach-100 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-peach-50">
            <div>
              <h3 className="font-semibold text-peach-900 text-sm">Get in touch</h3>
              <p className="text-xs text-peach-400 mt-0.5">We read every message.</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close contact panel"
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-peach-50 text-peach-300 hover:text-peach-500 transition-colors"
            >
              <X size={15} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            {sent ? (
              <div className="flex flex-col items-center text-center py-6 gap-3">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle size={24} className="text-green-500" />
                </div>
                <div>
                  <p className="font-semibold text-peach-800 text-sm">Thank you for your message!</p>
                  <p className="text-xs text-peach-400 mt-1">We truly appreciate you taking the time to reach out. Your feedback means a lot to us.</p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="btn-primary text-sm py-2 px-6 mt-1"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Topic */}
                <div>
                  <label className="label">Topic</label>
                  <select
                    className="input mt-1 text-sm"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                  >
                    {TOPICS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* Email */}
                <div>
                  <label className="label">Your email</label>
                  <input
                    type="email"
                    className="input mt-1 text-sm"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                {/* Message */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="label">Message</label>
                    <span className="text-xs text-peach-300">{message.length}/1000</span>
                  </div>
                  <textarea
                    className="input mt-1 resize-none text-sm"
                    rows={4}
                    placeholder="Tell us what's on your mind…"
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
                    required
                    autoFocus
                  />
                </div>

                {error && (
                  <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-xl">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !message.trim() || !email.trim()}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Send size={14} />
                  {submitting ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* FAB */}
      <button
        onClick={open ? () => setOpen(false) : handleOpen}
        className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 active:scale-95 pointer-events-auto"
        style={{ background: "linear-gradient(135deg, #ff8c5a 0%, #ff6b35 100%)" }}
        aria-label="Contact us"
      >
        <div
          className="transition-all duration-200"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
        >
          {open ? <X size={20} color="white" /> : <MessageCircle size={20} color="white" />}
        </div>
      </button>
    </div>
  );
}
